<script setup>
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import {
  Bars3Icon,
  CogIcon,
  CubeIcon,
  DocumentChartBarIcon,
  PowerIcon,
  RectangleGroupIcon,
  UserCircleIcon,
  UsersIcon,
  Cog8ToothIcon,
  PhoneIcon,
  BellAlertIcon,
} from "@heroicons/vue/24/solid";
import { Link, router, usePage } from "@inertiajs/vue3";
import FlashMessage from "@js/components/FlashMessage.vue";
import MenuButton from "@js/components/menus/MenuButton.vue";
import MenuDisclosure from "@js/components/menus/MenuDisclosure.vue";
import MenuGroup from "@js/components/menus/MenuGroup.vue";
import { hasRole } from "@js/utils/permission";
import { useRoute } from "@js/utils/route";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  showCustomSidebar: {
    type: Boolean,
    required: false,
    default: null,
  },
  toggleCustomSidebar: {
    type: Function,
    required: false,
    default: null,
  },
});

const { t } = useI18n();

const { route, routesAreActive } = useRoute();

const currentUser = computed(() => usePage().props.currentUser);

const callbackRequestToProcessCount = computed(
  () => usePage().props.callbackRequestToProcessCount,
);

const alertCount = computed(() => usePage().props.alertCount);

const showMobileSidebar = ref(false);

function toggleMobileSidebar() {
  showMobileSidebar.value = !showMobileSidebar.value;
}
</script>

<template>
  <FlashMessage />

  <nav
    class="fixed top-0 z-50 flex w-full items-center justify-between border-b border-gray-200 bg-white p-3"
  >
    <div class="flex items-center justify-start gap-4">
      <button
        type="button"
        class="cursor-pointer rounded-lg p-2 text-indigo-950 duration-150 hover:bg-indigo-600 hover:text-white xl:hidden"
        :class="{ 'bg-indigo-600 text-white': showMobileSidebar }"
        @click="toggleMobileSidebar"
      >
        <Bars3Icon class="size-6" />
      </button>

      <button
        v-if="props.toggleCustomSidebar"
        type="button"
        class="hidden cursor-pointer rounded-lg p-2 text-indigo-950 duration-150 hover:bg-indigo-600 hover:text-white xl:block"
        :class="{ 'bg-indigo-600 text-white': showCustomSidebar }"
        @click="props.toggleCustomSidebar"
      >
        <Bars3Icon class="size-6" />
      </button>

      <Link :href="route('front.index')">
        <img
          src="/images/logo/logo.svg"
          :alt="t('all.title.datakepler')"
          class="h-8 max-xl:hidden"
        />

        <img
          src="/images/logo/logotype.svg"
          :alt="t('all.title.datakepler')"
          class="h-8 xl:hidden"
        />
      </Link>

      <ul
        class="fixed left-0 top-[4.05rem] z-30 h-full w-64 gap-2 !overflow-visible overflow-y-auto bg-white px-3 py-4 max-xl:flex-col xl:static xl:flex xl:w-full xl:px-0 xl:py-0 xl:pt-0"
        :class="{ hidden: !showMobileSidebar, flex: showMobileSidebar }"
      >
        <MenuButton
          v-if="hasRole('super_admin') || hasRole('dashboard_viewer')"
          :title="t('all.title.dashboard')"
          :icon="RectangleGroupIcon"
          :href="route('front.dashboard')"
          :active="routesAreActive(['front.dashboard'])"
          :level="0"
        />

        <MenuButton
          v-if="hasRole('super_admin')"
          :title="t('all.title.modules')"
          :icon="CubeIcon"
          :href="route('front.modules')"
          :active="routesAreActive(['front.modules', 'front.updateModule'])"
          :level="0"
        />

        <MenuButton
          v-if="hasRole('super_admin')"
          :title="t('all.title.users')"
          :icon="UsersIcon"
          :href="route('front.users')"
          :active="
            routesAreActive([
              'front.users',
              'front.createUser',
              'front.updateUser',
              'front.createCustomerUser',
              'front.updateCustomerUser',
            ])
          "
          :level="0"
        />

        <MenuButton
          v-if="hasRole('super_admin')"
          :title="t('title.projectManagement')"
          :icon="CogIcon"
          :href="route('front.projectManagement.task.tasks')"
          :active="
            routesAreActive([
              'front.projectManagement.task.objectives',
              'front.projectManagement.task.tasks',
              'front.projectManagement.task.createTask',
              'front.projectManagement.task.createTaskIdea',
              'front.projectManagement.task.updateTask',
              'front.projectManagement.task.updateTaskIdea',
              'front.projectManagement.task.taskTimes',
              'front.projectManagement.task.customerTasks',
              'front.projectManagement.task.customerTask',
              'front.projectManagement.task.customerTaskIdea',
              'front.projectManagement.apikom.affiliations',
              'front.projectManagement.apikom.createAffiliation',
              'front.projectManagement.apikom.updateAffiliation',
              'front.projectManagement.apikom.quotations',
              'front.projectManagement.apikom.createQuotation',
              'front.projectManagement.apikom.updateQuotation',
              'front.projectManagement.apikom.invoicing',
              'front.projectManagement.apikom.packages',
              'front.projectManagement.apikom.createPackage',
              'front.projectManagement.apikom.updatePackage',
              'front.projectManagement.apikom.opportunities',
              'front.projectManagement.apikom.partners',
              'front.projectManagement.apikom.createPartner',
              'front.projectManagement.apikom.updatePartner',
              'front.projectManagement.apikom.prospects',
              'front.projectManagement.apikom.prospect',
              'front.projectManagement.apikom.appointments',
              'front.projectManagement.apikom.createAppointment',
              'front.projectManagement.apikom.updateAppointment',
              'front.projectManagement.customer.customersBudgets',
              'front.projectManagement.customer.contacts',
              'front.projectManagement.customer.createCustomer',
              'front.projectManagement.customer.updateCustomer',
              'front.projectManagement.customer.dev',
              'front.projectManagement.customer.customersServices',
              'front.projectManagement.customer.reports',
              'front.projectManagement.dev.eCommerceModules',
              'front.projectManagement.dev.pageSpeed',
              'front.projectManagement.dev.createPageSpeedUrl',
              'front.projectManagement.dev.servers',
              'front.projectManagement.dev.websites',
              'front.projectManagement.dev.website',
              'front.projectManagement.sea.audiences',
              'front.projectManagement.sea.audience',
              'front.projectManagement.sma.audience',
              'front.projectManagement.sma.audience',
              'front.projectManagement.seo.apikomContents',
              'front.projectManagement.seo.createApikomContent',
              'front.projectManagement.seo.updateApikomConte',
              'front.projectManagement.seo.eCommerceCategoryContents',
              'front.projectManagement.seo.eCommerceProductContents',
              'front.projectManagement.seo.devSeo',
              'front.projectManagement.seo.indexation',
              'front.projectManagement.seo.internalMeshLinks',
              'front.projectManagement.seo.createInternalMeshLink',
              'front.projectManagement.seo.updateInternalMeshLink',
              'front.projectManagement.seo.netlinking',
              'front.projectManagement.seo.createBacklink',
              'front.projectManagement.seo.updateBacklink',
              'front.projectManagement.seo.positionTracking',
              'front.projectManagement.seo.errors',
              'front.projectManagement.sem.banners',
              'front.projectManagement.sem.banner',
              'front.projectManagement.sem.emailings',
              'front.projectManagement.sem.emailing',
              'front.projectManagement.sem.shortMessageServices',
              'front.projectManagement.sem.shortMessageService',
              'front.projectManagement.sem.events',
              'front.projectManagement.sem.createEvent',
              'front.projectManagement.sem.event',
              'front.projectManagement.sem.tests',
              'front.projectManagement.sem.test',
              'front.projectManagement.sem.segmentations',
              'front.projectManagement.sem.segmentation',
              'front.projectManagement.sem.segmentationCustomers',
              'front.projectManagement.sem.lifeTimeValues',
              'front.projectManagement.docAndFaq',
              'front.projectManagement.faqs',
              'front.projectManagement.faq',
            ])
          "
          class="hidden xl:flex"
          :level="0"
        />

        <MenuDisclosure
          v-if="hasRole('super_admin')"
          :title="t('title.projectManagement')"
          :icon="CogIcon"
          :active="
            routesAreActive([
              'front.projectManagement.task.objectives',
              'front.projectManagement.task.tasks',
              'front.projectManagement.task.createTask',
              'front.projectManagement.task.createTaskIdea',
              'front.projectManagement.task.updateTask',
              'front.projectManagement.task.updateTaskIdea',
              'front.projectManagement.task.taskTimes',
              'front.projectManagement.task.customerTasks',
              'front.projectManagement.task.customerTask',
              'front.projectManagement.task.customerTaskIdea',
              'front.projectManagement.apikom.affiliations',
              'front.projectManagement.apikom.createAffiliation',
              'front.projectManagement.apikom.updateAffiliation',
              'front.projectManagement.apikom.quotations',
              'front.projectManagement.apikom.createQuotation',
              'front.projectManagement.apikom.updateQuotation',
              'front.projectManagement.apikom.invoicing',
              'front.projectManagement.apikom.packages',
              'front.projectManagement.apikom.createPackage',
              'front.projectManagement.apikom.updatePackage',
              'front.projectManagement.apikom.opportunities',
              'front.projectManagement.apikom.partners',
              'front.projectManagement.apikom.createPartner',
              'front.projectManagement.apikom.updatePartner',
              'front.projectManagement.apikom.prospects',
              'front.projectManagement.apikom.prospect',
              'front.projectManagement.apikom.appointments',
              'front.projectManagement.apikom.createAppointment',
              'front.projectManagement.apikom.updateAppointment',
              'front.projectManagement.customer.customersBudgets',
              'front.projectManagement.customer.contacts',
              'front.projectManagement.customer.createCustomer',
              'front.projectManagement.customer.updateCustomer',
              'front.projectManagement.customer.dev',
              'front.projectManagement.customer.customersServices',
              'front.projectManagement.customer.reports',
              'front.projectManagement.dev.eCommerceModules',
              'front.projectManagement.dev.pageSpeed',
              'front.projectManagement.dev.createPageSpeedUrl',
              'front.projectManagement.dev.servers',
              'front.projectManagement.dev.websites',
              'front.projectManagement.dev.website',
              'front.projectManagement.sea.audiences',
              'front.projectManagement.sea.audience',
              'front.projectManagement.sma.audience',
              'front.projectManagement.sma.audience',
              'front.projectManagement.seo.apikomContents',
              'front.projectManagement.seo.createApikomContent',
              'front.projectManagement.seo.updateApikomConte',
              'front.projectManagement.seo.eCommerceCategoryContents',
              'front.projectManagement.seo.eCommerceProductContents',
              'front.projectManagement.seo.devSeo',
              'front.projectManagement.seo.indexation',
              'front.projectManagement.seo.internalMeshLinks',
              'front.projectManagement.seo.createInternalMeshLink',
              'front.projectManagement.seo.updateInternalMeshLink',
              'front.projectManagement.seo.netlinking',
              'front.projectManagement.seo.createBacklink',
              'front.projectManagement.seo.updateBacklink',
              'front.projectManagement.seo.positionTracking',
              'front.projectManagement.seo.errors',
              'front.projectManagement.sem.banners',
              'front.projectManagement.sem.banner',
              'front.projectManagement.sem.emailings',
              'front.projectManagement.sem.emailing',
              'front.projectManagement.sem.shortMessageServices',
              'front.projectManagement.sem.shortMessageService',
              'front.projectManagement.sem.events',
              'front.projectManagement.sem.createEvent',
              'front.projectManagement.sem.event',
              'front.projectManagement.sem.tests',
              'front.projectManagement.sem.test',
              'front.projectManagement.sem.segmentations',
              'front.projectManagement.sem.segmentation',
              'front.projectManagement.sem.segmentationCustomers',
              'front.projectManagement.sem.lifeTimeValues',
            ])
          "
          :level="0"
          class="flex xl:hidden"
        >
          <MenuGroup v-slot="{ setCurrentDisclosure }">
            <MenuDisclosure
              :title="t('title.tasks')"
              :active="
                routesAreActive([
                  'front.projectManagement.task.objectives',
                  'front.projectManagement.task.tasks',
                  'front.projectManagement.task.createTask',
                  'front.projectManagement.task.createTaskIdea',
                  'front.projectManagement.task.updateTask',
                  'front.projectManagement.task.updateTaskIdea',
                  'front.projectManagement.task.taskTimes',
                  'front.projectManagement.task.customerTasks',
                  'front.projectManagement.task.customerTask',
                  'front.projectManagement.task.customerTaskIdea',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.apikomTasks')"
                :href="route('front.projectManagement.task.tasks')"
                :active="
                  routesAreActive([
                    'front.projectManagement.task.tasks',
                    'front.projectManagement.task.createTask',
                    'front.projectManagement.task.createTaskIdea',
                    'front.projectManagement.task.updateTask',
                    'front.projectManagement.task.updateTaskIdea',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.apikomTime')"
                :href="route('front.projectManagement.task.taskTimes')"
                :active="
                  routesAreActive(['front.projectManagement.task.taskTimes'])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.customerTasks')"
                :href="route('front.projectManagement.task.customerTasks')"
                :active="
                  routesAreActive([
                    'front.projectManagement.task.customerTasks',
                    'front.projectManagement.task.customerTask',
                    'front.projectManagement.task.customerTaskIdea',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.objectives')"
                :href="route('front.projectManagement.task.objectives')"
                :active="
                  routesAreActive(['front.projectManagement.task.objectives'])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.apikom')"
              :active="
                routesAreActive([
                  'front.projectManagement.apikom.affiliations',
                  'front.projectManagement.apikom.createAffiliation',
                  'front.projectManagement.apikom.updateAffiliation',
                  'front.projectManagement.apikom.quotations',
                  'front.projectManagement.apikom.createQuotation',
                  'front.projectManagement.apikom.updateQuotation',
                  'front.projectManagement.apikom.invoicing',
                  'front.projectManagement.apikom.packages',
                  'front.projectManagement.apikom.createPackage',
                  'front.projectManagement.apikom.updatePackage',
                  'front.projectManagement.apikom.opportunities',
                  'front.projectManagement.apikom.partners',
                  'front.projectManagement.apikom.createPartner',
                  'front.projectManagement.apikom.updatePartner',
                  'front.projectManagement.apikom.prospects',
                  'front.projectManagement.apikom.prospect',
                  'front.projectManagement.apikom.appointments',
                  'front.projectManagement.apikom.createAppointment',
                  'front.projectManagement.apikom.updateAppointment',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.affiliations')"
                :href="route('front.projectManagement.apikom.affiliations')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.affiliations',
                    'front.projectManagement.apikom.createAffiliation',
                    'front.projectManagement.apikom.updateAffiliation',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.quotation')"
                :href="route('front.projectManagement.apikom.quotations')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.quotations',
                    'front.projectManagement.apikom.createQuotation',
                    'front.projectManagement.apikom.updateQuotation',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.invoicing')"
                :href="route('front.projectManagement.apikom.invoicing')"
                :active="
                  routesAreActive(['front.projectManagement.apikom.invoicing'])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.packages')"
                :href="route('front.projectManagement.apikom.packages')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.packages',
                    'front.projectManagement.apikom.createPackage',
                    'front.projectManagement.apikom.updatePackage',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.opportunities')"
                :href="route('front.projectManagement.apikom.opportunities')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.opportunities',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.partners')"
                :href="route('front.projectManagement.apikom.partners')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.partners',
                    'front.projectManagement.apikom.createPartner',
                    'front.projectManagement.apikom.updatePartner',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.prospects')"
                :href="route('front.projectManagement.apikom.prospects')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.prospects',
                    'front.projectManagement.apikom.prospect',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('abbreviation.appointment')"
                :href="route('front.projectManagement.apikom.appointments')"
                :active="
                  routesAreActive([
                    'front.projectManagement.apikom.appointments',
                    'front.projectManagement.apikom.createAppointment',
                    'front.projectManagement.apikom.updateAppointment',
                  ])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.customers')"
              :active="
                routesAreActive([
                  'front.projectManagement.customer.customersBudgets',
                  'front.projectManagement.customer.contacts',
                  'front.projectManagement.customer.createCustomer',
                  'front.projectManagement.customer.updateCustomer',
                  'front.projectManagement.customer.dev',
                  'front.projectManagement.customer.customersServices',
                  'front.projectManagement.customer.reports',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.budgets')"
                :href="
                  route('front.projectManagement.customer.customersBudgets')
                "
                :active="
                  routesAreActive([
                    'front.projectManagement.customer.customersBudgets',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.contacts')"
                :href="
                  route('front.projectManagement.customer.contacts')
                "
                :active="
                  routesAreActive([
                    'front.projectManagement.customer.contacts',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.dev')"
                :href="route('front.projectManagement.customer.dev')"
                :active="
                  routesAreActive(['front.projectManagement.customer.dev'])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.services')"
                :href="
                  route('front.projectManagement.customer.customersServices')
                "
                :active="
                  routesAreActive([
                    'front.projectManagement.customer.customersServices',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.reports')"
                :href="route('front.projectManagement.customer.reports')"
                :active="
                  routesAreActive(['front.projectManagement.customer.reports'])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.dev')"
              :active="
                routesAreActive([
                  'front.projectManagement.dev.eCommerceModules',
                  'front.projectManagement.dev.pageSpeed',
                  'front.projectManagement.dev.createPageSpeedUrl',
                  'front.projectManagement.dev.servers',
                  'front.projectManagement.dev.websites',
                  'front.projectManagement.dev.website',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.eCommerceModules')"
                :href="route('front.projectManagement.dev.eCommerceModules')"
                :active="
                  routesAreActive([
                    'front.projectManagement.dev.eCommerceModules',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.pageSpeed')"
                :href="route('front.projectManagement.dev.pageSpeed')"
                :active="
                  routesAreActive([
                    'front.projectManagement.dev.pageSpeed',
                    'front.projectManagement.dev.createPageSpeedUrl',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.servers')"
                :href="route('front.projectManagement.dev.servers')"
                :active="
                  routesAreActive(['front.projectManagement.dev.servers'])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.websites')"
                :href="route('front.projectManagement.dev.websites')"
                :active="
                  routesAreActive([
                    'front.projectManagement.dev.websites',
                    'front.projectManagement.dev.website',
                  ])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.sea')"
              :active="
                routesAreActive([
                  'front.projectManagement.sea.audiences',
                  'front.projectManagement.sea.audience',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.audiences')"
                :href="route('front.projectManagement.sea.audiences')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sea.audiences',
                    'front.projectManagement.sea.audience',
                  ])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.sma')"
              :active="
                routesAreActive([
                  'front.projectManagement.sma.audiences',
                  'front.projectManagement.sma.audience',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.audiences')"
                :href="route('front.projectManagement.sma.audiences')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sma.audiences',
                    'front.projectManagement.sma.audience',
                  ])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.seo')"
              :active="
                routesAreActive([
                  'front.projectManagement.seo.apikomContents',
                  'front.projectManagement.seo.createApikomContent',
                  'front.projectManagement.seo.updateApikomConte',
                  'front.projectManagement.seo.eCommerceCategoryContents',
                  'front.projectManagement.seo.eCommerceProductContents',
                  'front.projectManagement.seo.devSeo',
                  'front.projectManagement.seo.indexation',
                  'front.projectManagement.seo.internalMeshLinks',
                  'front.projectManagement.seo.createInternalMeshLink',
                  'front.projectManagement.seo.updateInternalMeshLink',
                  'front.projectManagement.seo.netlinking',
                  'front.projectManagement.seo.createBacklink',
                  'front.projectManagement.seo.updateBacklink',
                  'front.projectManagement.seo.positionTracking',
                  'front.projectManagement.seo.errors',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.apikomContents')"
                :href="route('front.projectManagement.seo.apikomContents')"
                :active="
                  routesAreActive([
                    'front.projectManagement.seo.apikomContents',
                    'front.projectManagement.seo.createApikomContent',
                    'front.projectManagement.seo.updateApikomConte',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.categoryContents')"
                :href="
                  route('front.projectManagement.seo.eCommerceCategoryContents')
                "
                :active="
                  routesAreActive([
                    'front.projectManagement.seo.eCommerceCategoryContents',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.productContents')"
                :href="
                  route('front.projectManagement.seo.eCommerceProductContents')
                "
                :active="
                  routesAreActive([
                    'front.projectManagement.seo.eCommerceProductContents',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.devSeo')"
                :href="route('front.projectManagement.seo.devSeo')"
                :active="
                  routesAreActive(['front.projectManagement.seo.devSeo'])
                "
                :level="2"
              />

              <!--              <MenuButton-->
              <!--                :title="t('title.errors')"-->
              <!--                :href="route('front.projectManagement.seo.errors')"-->
              <!--                :active="-->
              <!--                  routesAreActive(['front.projectManagement.seo.errors'])-->
              <!--                "-->
              <!--                :level="2"-->
              <!--              />-->

              <MenuButton
                :title="t('title.indexation')"
                :href="route('front.projectManagement.seo.indexation')"
                :active="
                  routesAreActive(['front.projectManagement.seo.indexation'])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.internalMesh')"
                :href="route('front.projectManagement.seo.internalMeshLinks')"
                :active="
                  routesAreActive([
                    'front.projectManagement.seo.internalMeshLinks',
                    'front.projectManagement.seo.createInternalMeshLink',
                    'front.projectManagement.seo.updateInternalMeshLink',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.netlinking')"
                :href="route('front.projectManagement.seo.netlinking')"
                :active="
                  routesAreActive([
                    'front.projectManagement.seo.netlinking',
                    'front.projectManagement.seo.createBacklink',
                    'front.projectManagement.seo.updateBacklink',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.positionTracking')"
                :href="route('front.projectManagement.seo.positionTracking')"
                :active="
                  routesAreActive([
                    'front.projectManagement.seo.positionTracking',
                  ])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuDisclosure
              :title="t('title.sem')"
              :active="
                routesAreActive([
                  'front.projectManagement.sem.banners',
                  'front.projectManagement.sem.banner',
                  'front.projectManagement.sem.emailings',
                  'front.projectManagement.sem.emailing',
                  'front.projectManagement.sem.shortMessageServices',
                  'front.projectManagement.sem.shortMessageService',
                  'front.projectManagement.sem.events',
                  'front.projectManagement.sem.createEvent',
                  'front.projectManagement.sem.event',
                  'front.projectManagement.sem.tests',
                  'front.projectManagement.sem.test',
                  'front.projectManagement.sem.segmentations',
                  'front.projectManagement.sem.segmentation',
                  'front.projectManagement.sem.segmentationCustomers',
                  'front.projectManagement.sem.lifeTimeValues',
                ])
              "
              :level="1"
              :set-current-disclosure="setCurrentDisclosure"
            >
              <MenuButton
                :title="t('title.banners')"
                :href="route('front.projectManagement.sem.banners')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.banners',
                    'front.projectManagement.sem.banner',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.events')"
                :href="route('front.projectManagement.sem.emailings')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.emailings',
                    'front.projectManagement.sem.emailing',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.events')"
                :href="route('front.projectManagement.sem.events')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.events',
                    'front.projectManagement.sem.createEvent',
                    'front.projectManagement.sem.event',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.lifeTimeValues')"
                :href="route('front.projectManagement.sem.lifeTimeValues')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.lifeTimeValues',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.sms')"
                :href="
                  route('front.projectManagement.sem.shortMessageServices')
                "
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.shortMessageServices',
                    'front.projectManagement.sem.shortMessageService',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.segmentations')"
                :href="route('front.projectManagement.sem.segmentations')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.segmentations',
                    'front.projectManagement.sem.segmentation',
                    'front.projectManagement.sem.segmentationCustomers',
                  ])
                "
                :level="2"
              />

              <MenuButton
                :title="t('title.userTests')"
                :href="route('front.projectManagement.sem.tests')"
                :active="
                  routesAreActive([
                    'front.projectManagement.sem.tests',
                    'front.projectManagement.sem.test',
                  ])
                "
                :level="2"
              />
            </MenuDisclosure>

            <MenuButton
              :title="t('title.alerts')"
              :href="route('front.projectManagement.alerts')"
              :active="routesAreActive(['front.projectManagement.alerts'])"
              :level="1"
            />

            <MenuButton
              :title="t('title.docAndFaq')"
              :href="route('front.projectManagement.docAndFaq')"
              :active="
                routesAreActive([
                  'front.projectManagement.docAndFaq',
                  'front.projectManagement.faqs',
                  'front.projectManagement.faq',
                ])
              "
              :level="1"
            />
          </MenuGroup>
        </MenuDisclosure>

        <MenuButton
          v-if="hasRole('super_admin')"
          :title="t('all.title.logs')"
          :icon="DocumentChartBarIcon"
          :href="route('front.logs')"
          :active="routesAreActive(['front.logs'])"
          :level="0"
        />

        <MenuButton
          v-if="hasRole('super_admin')"
          :title="t('title.callbackRequests')"
          :icon="PhoneIcon"
          :href="route('front.callbackRequests')"
          :active="routesAreActive(['front.callbackRequests'])"
          :level="0"
          :pin-value="callbackRequestToProcessCount"
          :ping-type="callbackRequestToProcessCount > 0 ? 'danger' : 'success'"
          :animate-ping="callbackRequestToProcessCount > 0"
        />

        <MenuButton
          v-if="hasRole('super_admin')"
          :title="t('title.alerts')"
          :href="route('front.alerts')"
          :active="routesAreActive(['front.alerts'])"
          :level="0"
          :icon="BellAlertIcon"
          :pin-value="alertCount"
          :ping-type="alertCount === 0 ? 'success' : 'danger'"
          :animate-ping="alertCount !== 0"
        />
      </ul>
    </div>

    <div class="flex items-center gap-4">
      <Popover class="relative flex items-center">
        <PopoverButton class="focus:outline-none">
          <UserCircleIcon class="h-10 w-10 text-gray-300" />
        </PopoverButton>

        <PopoverPanel
          v-slot="{ close }"
          class="absolute right-0 top-12 z-10 rounded bg-white drop-shadow"
        >
          <div class="px-4 py-3">
            <p class="text-sm text-gray-900">
              {{ currentUser.lastname }} {{ currentUser.firstname }}
            </p>

            <p class="truncate text-sm font-medium text-gray-900">
              {{ currentUser.email }}
            </p>
          </div>

          <ul class="flex flex-col gap-px bg-gray-100 pt-px">
            <li v-if="hasRole('super_admin')">
              <button
                class="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                :class="{
                  'bg-white': !routesAreActive(['front.settings']),
                  'bg-gray-100': routesAreActive(['front.settings']),
                }"
                @click="
                  () => {
                    close();
                    router.visit(route('front.settings'));
                  }
                "
              >
                <Cog8ToothIcon class="size-4" />

                {{ t("title.settings") }}
              </button>
            </li>

            <li>
              <a
                :href="route('front.logout')"
                class="flex w-full items-center gap-2 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <PowerIcon class="size-4" />
                {{ t("all.link.logout") }}
              </a>
            </li>
          </ul>
        </PopoverPanel>
      </Popover>
    </div>
  </nav>

  <div
    class="fixed left-0 top-0 z-20 block h-screen w-screen cursor-pointer bg-black opacity-50"
    :class="{ hidden: !showMobileSidebar }"
    @click="toggleMobileSidebar"
  ></div>

  <div class="min-h-screen">
    <div class="mt-16 bg-gray-100 p-4">
      <slot />
    </div>
  </div>
</template>
